import { callRpc } from "./client";
import { filtroEscopoEmpresaSessao } from "./escopoEmpresa";
import type { Empresa } from "../types/empresa";
import type { UsuarioSessao } from "../types/auth";
import { somenteCadastrosAtivos } from "../utils/cadastrosAtivos";

export interface FiltroEmpresa {
  codOrganizacao?: string | number;
  codEmpresa?: string | number;
}

export interface EmpresaOrganizacaoOpcao {
  codigo: string | number;
  codOrganizacao?: string | number;
  nome: string;
  cnpj?: string;
  status?: string;
  /** Logo da empresa. Vazio quando não há: o seletor cai no avatar de iniciais. */
  logoUrl?: string;
}

/**
 * Lista empresas via RPC legado obterEmpresasCadastroEmpresa(filtro).
 * O back-end devolve diretamente um array (rows.map(empresaPayload)).
 */
export async function listarEmpresas(filtro: FiltroEmpresa = {}): Promise<Empresa[]> {
  const res = await callRpc<unknown>("obterEmpresasCadastroEmpresa", filtro);
  if (Array.isArray(res)) return res as Empresa[];
  if (res && typeof res === "object" && Array.isArray((res as { dados?: unknown }).dados)) {
    return (res as { dados: Empresa[] }).dados;
  }
  return [];
}

export async function listarEmpresasAtivas(filtro: FiltroEmpresa = {}): Promise<Empresa[]> {
  return somenteCadastrosAtivos(await listarEmpresas(filtro));
}

/**
 * Catálogo para estoque e configuração de acessos: não herda a empresa ativa.
 * "todos" apenas solicita as opções; o servidor aplica as autorizações do login.
 */
export function listarEmpresasAtivasDoLogin(
  usuario: UsuarioSessao | null,
  codOrganizacao?: string | number,
): Promise<Empresa[]> {
  return listarEmpresasAtivas({
    codOrganizacao: codOrganizacao ?? filtroPadraoEmpresa(usuario).codOrganizacao,
    codEmpresa: "todos",
  });
}

/**
 * Filtro das listagens da organização/empresa selecionada na sessão.
 * Estoque e configuração de acessos usam listarEmpresasAtivasDoLogin.
 */
export function filtroPadraoEmpresa(usuario: UsuarioSessao | null): FiltroEmpresa {
  return filtroEscopoEmpresaSessao(usuario);
}

export async function listarEmpresasDaOrganizacao(
  codOrganizacao?: string | number
): Promise<EmpresaOrganizacaoOpcao[]> {
  const res = await callRpc<{ sucesso?: boolean; dados?: EmpresaOrganizacaoOpcao[] }>(
    "obterEmpresasDaOrganizacao",
    codOrganizacao ?? ""
  );
  return res?.dados ?? [];
}

export async function listarEmpresasAtivasDaOrganizacao(
  codOrganizacao?: string | number
): Promise<EmpresaOrganizacaoOpcao[]> {
  return somenteCadastrosAtivos(await listarEmpresasDaOrganizacao(codOrganizacao));
}

export type DadosEmpresa = Record<string, unknown>;

interface RespostaSalvar {
  sucesso?: boolean;
  erro?: string;
  codEmpresa?: string;
  codCliente?: string;
}

export function salvarEmpresa(dados: DadosEmpresa): Promise<RespostaSalvar> {
  return callRpc<RespostaSalvar>("salvarEmpresaCadastroEmpresa", dados);
}

export function editarEmpresa(dados: DadosEmpresa): Promise<RespostaSalvar> {
  return callRpc<RespostaSalvar>("editarEmpresaCadastroEmpresa", dados);
}

export function excluirEmpresas(
  itens: Array<Record<string, unknown>>
): Promise<{ sucesso?: boolean; erro?: string }> {
  return callRpc("excluirEmpresasCadastroEmpresa", itens);
}

export function alternarFavoritoEmpresa(
  params: Record<string, unknown>
): Promise<{ sucesso?: boolean; favorito?: unknown }> {
  return callRpc("marcarFavoritoCadEmpresa", params);
}

export interface RespostaImportacao {
  sucesso?: boolean;
  erro?: string;
  importados?: number;
  ignorados?: number;
}

export function importarEmpresas(payload: {
  base64: string;
  fileName: string;
  codOrg: string | number;
}): Promise<RespostaImportacao> {
  return callRpc<RespostaImportacao>("importarEmpresasCadastroEmpresa", payload);
}

/* ---- Imagem (logo) — atuam direto no registro existente (por id) ---- */
export function salvarImagemEmpresa(
  base64: string,
  id: string | number,
  codOrg: string | number
): Promise<{ sucesso?: boolean; id?: string; url?: string; erro?: string }> {
  return callRpc("salvarImagemEmpresa", base64, id, codOrg);
}

export function removerImagemEmpresa(
  id: string | number,
  codOrg: string | number
): Promise<{ sucesso?: boolean; erro?: string }> {
  return callRpc("removerImagemEmpresa", id, codOrg);
}

/* ---- Anexos ---- */
export interface AnexoEmpresa {
  id: string;
  name: string;
  url: string;
  mimeType?: string;
  size?: number;
  createdAt?: string;
}

export async function listarAnexosEmpresa(
  codOrg: string | number,
  codEmpresa: string | number
): Promise<AnexoEmpresa[]> {
  const res = await callRpc<{ sucesso?: boolean; arquivos?: AnexoEmpresa[] }>(
    "listarAnexosEmpresaServidor",
    { codOrg, codEmpresa, codCliente: codEmpresa }
  );
  return res?.arquivos ?? [];
}

export function salvarAnexoEmpresa(payload: {
  base64: string;
  nomeArquivo: string;
  codEmpresa: string | number;
  codOrg: string | number;
  nomeEmpresa?: string;
}): Promise<{ sucesso?: boolean; id?: string; url?: string; erro?: string }> {
  return callRpc("salvarAnexoEmpresaServidor", {
    ...payload,
    codCliente: payload.codEmpresa,
    fileName: payload.nomeArquivo,
  });
}

export function excluirAnexoEmpresa(
  caminho: string
): Promise<{ sucesso?: boolean; erro?: string }> {
  return callRpc("excluirAnexoEmpresaServidor", caminho);
}
